import * as React from "react";
import { PensionType } from "../../../../../../../../Wrap/Constants/Enums/Domain/PensionType";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../../Shared/Process/Process/ProcessComponent";
import { ApplyModel } from "../../../../../../../../Wrap/Models/Models/Processes/Apply/ApplyModel";
import { SourceOfWealth } from "../../../../Common/PaymentsIn/SIPP/SourceOfWealth";
import { RetirementAge } from "../../../../Common/PaymentsIn/SIPP/RetirementAge";
import { PaymentType, processTypeEnum } from "../../../../Common/Resource/Contents";
import { nbPaymentsInMessages } from "../../../Resource/Contents";
import { SinglePayment } from "../../../../Common/PaymentsIn/SinglePayment/SinglePayment";
import { RegularPayment } from "../../../../Common/PaymentsIn/RegularPayment/RegularPayment";
import { MoneyPurchaseAnnualAllowanceComponent } from "../../../../Common/PaymentsIn/SIPP/MoneyPurchaseAnnualAllowanceComponent";
import { PensionProtection } from "../../../../Common/PaymentsIn/SIPP/PensionProtection";
import { TransferIn } from "../../../../Common/PaymentsIn/TransferIn/TransferIn";
import { SubAccountTypeHelper } from "../../../../Common/Helpers/SubAccountTypeHelper";
import { EWizardLinterStatus, WizardLinter } from "../../../../../../../Shared/Process/Process/WizardLinter";
import { EWizardLinterHelper } from "../../../Resource/EWizardLinterHelper";
import { SubAccountType } from "../../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ProcessType } from "../../../../../../../Shared/IST/Resource/Content";
import { FinancialAdvice } from "../../../../Common/PaymentsIn/FinancialAdvice";
import { needSingleContribution, needPostTransfer } from "../../../../Common/PaymentsIn/SIPP/ValidationHelper";
import { RestrictionStore } from "../../../../Common/Restrictions/Variable";
import { RestrictionHelper } from "../../../../../../../Shared/Common/RestrictionHelper";
import { BankAccountAction } from "../../../../Common/PaymentsIn/BankAccount/BankAccountAction";
import { ProcessTypeHelper } from "../../../../Common/Helpers/ProcessTypeHelper";
import { inUseBankAccountForApply } from "../../../../Common/PaymentsIn/BankAccount/BankAccountHelper";

interface IPaymentsInProps extends IProcessComponentProps {
    data: ApplyModel;
    processType?: ProcessType;
    HasExistingSIPP?: boolean;
    transferDisabled?: boolean;
}

interface IPaymentsInState {}

export class PaymentsIn extends ProcessComponent<IPaymentsInProps, IPaymentsInState> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    private getProps = (key) => {
        const { onChange, data, showErrorMsgPopUp, isReview, processType } = this.props;
        return {
            type: key,
            data: data[key],
            isReview: isReview,
            onRef: (ref) => this.appendChild(ref),
            showErrorMsgPopUp: (msg, isShow) => showErrorMsgPopUp(msg, isShow),
            onChange: (changes) => this.onDataChange(onChange, changes, data, key),
            onSaveNewBankAccountInstruction: (bankAccountInstruction) => {
                bankAccountInstruction.OwnerHierarchyId = data.OwnerHierarchyId;
                BankAccountAction.saveBankInstruction(
                    bankAccountInstruction,
                    data.ClientDetails?.ActorId,
                    ProcessTypeHelper.getProcessTypeEnum(processType),
                    (savedBankAccountInstruction) => {
                        const changes = [...data.BankAccountInstructions, ...[savedBankAccountInstruction]];
                        this.onDataChange(onChange, changes, data, "BankAccountInstructions");
                    }
                );
            },
            onRemoveBankAccountInstruction: (bankAccountInstruction) => {
                BankAccountAction.removeBankInstruction(bankAccountInstruction, () => {
                    const changes = (data.BankAccountInstructions || []).filter(
                        (x) => x.AccountNumber != bankAccountInstruction.AccountNumber
                    );
                    this.onDataChange(onChange, { BankAccountInstructions: changes }, data);
                });
            },
            inUseBankAccountInstruction: inUseBankAccountForApply(data),
        };
    };

    public isValid(): boolean {
        let isValid = super.isValid();
        isValid = isValid && this.atLeastOneContribution();
        const config = this.props.data.SelectedProducts.find((ele) =>
            SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType)
        );
        if (!this.atLeastOneContribution()) {
            this.props.showErrorMsgPopUp([nbPaymentsInMessages.atLeastOneContribution]);
        }
        const needSingleContributionFlag = needSingleContribution(config, this.props.data.PensionApply);
        if (needSingleContributionFlag) {
            this.props.showErrorMsgPopUp([
                "Please add a contribution/transfer to support <Take Pension Benefits/Immediate Tax Free Cash>, or remove the SIPP Option",
            ]);
        }
        const needPostTransferFlag = needPostTransfer(config, this.props.data.PensionApply);
        if (needPostTransferFlag) {
            this.props.showErrorMsgPopUp([
                "Please add a Post Retirement Transfer, or remove the request to take income from the pension",
            ]);
        }
        return isValid && !needPostTransferFlag && !needSingleContributionFlag;
    }

    private atLeastOneContribution(): boolean {
        const { data } = this.props;

        return (
            data.PensionApply.ClientSinglePayment?.Money?.Amount > 0 ||
            data.PensionApply.ThirdPartySinglePayment?.Money?.Amount > 0 ||
            data.PensionApply.EmployerSinglePayment?.Money?.Amount > 0 ||
            data.PensionApply.ClientRegularPayment?.Money?.Amount > 0 ||
            data.PensionApply.ThirdPartyRegularPayment?.Money?.Amount > 0 ||
            data.PensionApply.EmployerRegularPayment?.Money?.Amount > 0 ||
            data.PensionApply.PensionTransfers?.length > 0
        );
    }
    getBlockStatus(): EWizardLinterStatus {
        const { data } = this.props;
        const complete =
            data?.AnnuityAssumptions?.PlannedRetirementDate != null &&
            data?.PensionApply?.MoneyPurchaseAnnualAllowance != null &&
            (data.PensionApply.MoneyPurchaseAnnualAllowance.Triggered == null ||
                data.PensionApply.MoneyPurchaseAnnualAllowance.Triggered == false ||
                (data.PensionApply.MoneyPurchaseAnnualAllowance.DateTriggered != null &&
                    data.PensionApply.MoneyPurchaseAnnualAllowance.Source != null)) &&
            data?.PensionApply?.HasPensionProtection != null;
        return EWizardLinterHelper.getEWizardLinterStatus(complete);
    }

    public render(): JSX.Element {
        const { onChange, data } = this.props;
        const {
            SelectedProducts,
            ClientDetails,
            AllBankAccounts,
            BankAccountInstructions,
            PensionApply,
        } = this.props.data;
        const isImmediateTaxFreeLumpSum =
            SelectedProducts.find((ele) => SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType))?.PensionType ==
            PensionType.ImmediateTaxFreeLumpSum;
        const pensionInfo = SelectedProducts.find((ele) =>
            SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType)
        );
        const isTransferPost =
            pensionInfo.PensionType == PensionType.None && pensionInfo.IsIncomePaymentDetailsSelected;
        return (
            <>
                <WizardLinter
                    status={EWizardLinterStatus.PleaseCheck}
                    enabled={EWizardLinterHelper.shouldDisplay(SubAccountType.Npr)}
                >
                    <h2>Payments in</h2>

                    <RetirementAge {...this.getProps("AnnuityAssumptions")} clientInfo={ClientDetails} />
                    <p className="mb-24">This age/date is used for illustration purposes.</p>

                    {(this.props.HasExistingSIPP != true || data.AdviceGiven == true) && (
                        <MoneyPurchaseAnnualAllowanceComponent {...this.getProps("PensionApply")} />
                    )}
                    <PensionProtection {...this.getProps("PensionApply")} label={"Edit pension protection"} />
                </WizardLinter>

                <hr />

                <SinglePayment
                    {...this.getProps("PensionApply")}
                    clientInfo={ClientDetails}
                    checkBoxOption={[PaymentType.Client, PaymentType.ThirdParty, PaymentType.Employer]}
                    subAccountType={pensionInfo.SubAccountType}
                    bankAccounts={AllBankAccounts}
                    hasPaymentMethod={true}
                    bankAccountInstructions={BankAccountInstructions}
                    processType={
                        this.props.processType == ProcessType.NB
                            ? processTypeEnum.NewBusiness
                            : processTypeEnum.NewBusinessExistingClient
                    }
                    HasExistingSIPP={this.props.HasExistingSIPP}
                />
                <hr />
                {!isImmediateTaxFreeLumpSum && (
                    <RegularPayment
                        {...this.getProps("PensionApply")}
                        clientInfo={ClientDetails}
                        bankAccounts={AllBankAccounts}
                        bankAccountInstructions={BankAccountInstructions}
                        checkBoxOption={[PaymentType.Client, PaymentType.ThirdParty, PaymentType.Employer]}
                        subAccountType={pensionInfo.SubAccountType}
                        hasPaymentMethod
                        processType={
                            this.props.processType == ProcessType.NB
                                ? processTypeEnum.NewBusiness
                                : processTypeEnum.NewBusinessExistingClient
                        }
                        HasExistingSIPP={this.props.HasExistingSIPP}
                    />
                )}
                <hr />

                <TransferIn
                    subAccountType={pensionInfo.SubAccountType}
                    data={PensionApply.PensionTransfers}
                    jouneryType={
                        this.props.processType == ProcessType.NB
                            ? processTypeEnum.NewBusiness
                            : processTypeEnum.NewBusinessExistingClient
                    }
                    onChange={(changes) =>
                        this.onDataChange(
                            (changes) => this.onDataChange(onChange, changes, data, "PensionApply"),
                            changes,
                            PensionApply
                        )
                    }
                    disabled={
                        RestrictionStore.transferDisabled() &&
                        RestrictionHelper.shouldHandleRestriction(this.props.processType)
                    }
                    HasExistingSIPP={this.props.HasExistingSIPP}
                    isReview={this.props.isReview}
                    showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                    isPost={isTransferPost}
                    onRef={(ref) => this.appendChild(ref)}
                    selectedProducts={this.props.data.SelectedProducts}
                    accountServiceProposition={this.props.data.AccountServiceProposition}
                />

                {PensionApply.ClientSinglePayment !== null && this.props.processType != ProcessType.NBEC && (
                    <>
                        <hr className="mt-32" />
                        <SourceOfWealth {...this.getProps("PensionApply")} />
                    </>
                )}
            </>
        );
    }
}
